<script lang="ts" setup>
  import { computed, onMounted, ref, useTemplateRef } from 'vue'
  import type { Ref } from 'vue'

  import { capitalize } from 'es-toolkit'

  import { useFirebaseApp, useFirestore } from 'vuefire'

  import { doc, setDoc } from 'firebase/firestore'
  import { getFunctions, httpsCallable } from 'firebase/functions'

  import useOuraAuth from '#web-oura-tools/auth'

  import { useServicesStore } from '#ourahub-intra/stores/services'

  import {
    ADDABLE_STAGES,
    PUBLISH_REQUESTABLE_STAGES,
    PUBLISH_STAGES,
    STAGE_VERSION_NAMES,
    applicationNameRule,
    applicationSummaryRule,
    contextTypes,
    existingIconRule,
    existingIconsRule,
    existingNameRule,
    requiredRule,
  } from '#ourahub-intra/constants'

  import { gcpRegion } from '#vue-shared-lib/utils/cloud-env'

  import type { Service } from '#ourahub-intra/types/services'
  import type { AppEnvironment } from '#web-oura-tools/types/auth'

  const props = defineProps<{
    service: Service
    // Forces the type dropdown open (intro tour only).
    openTypeMenu?: boolean
    scrollToPublishing?: boolean
  }>()

  const emits = defineEmits<{
    close: []
    publish: [stage: AppEnvironment]
  }>()

  const ouraAuth = useOuraAuth()

  const firestore = useFirestore()

  const firebaseApp = useFirebaseApp()

  const servicesStore = useServicesStore()

  const isSaving = ref(false)
  const isFormValid = ref(false)

  const localTypeMenu = ref(false)
  const typeMenuOpen = computed({
    get: () => props.openTypeMenu || localTypeMenu.value,
    set: (value) => (localTypeMenu.value = value),
  })

  const draftName = ref(props.service.name ?? '')
  const draftIcon = ref(props.service.icon ?? '')
  const draftContext = ref(props.service.context ?? '')
  const draftSummary = ref(props.service.summary ?? '')
  const draftCategory = ref(props.service.category ?? '')
  const draftDescription = ref(props.service.description ?? '')

  const draftOwner = ref(props.service.owner ?? '')
  const draftEditors = ref<string[]>([...(props.service.editors ?? [])])

  const ouraEmailRegex = /^[^\s@]+@ouraring\.com$/

  const isOwner = computed(() => props.service.owner === ouraAuth.user.value?.email)

  const otherServices = computed(() => servicesStore.servicesList.filter((s) => s.id !== props.service.id))

  const materialIcons: Ref<{ title: string; value: string; prependIcon: string }[]> = ref([])

  const publishingRef = useTemplateRef('publishing')

  onMounted(async () => {
    if (props.scrollToPublishing) {
      publishingRef.value?.scrollIntoView({ behavior: 'smooth' })
    }

    const MaterialIcons = await import('@mdi/svg/meta.json')

    materialIcons.value = MaterialIcons.default.map((i) => ({
      title: `${capitalize(i.name).replaceAll('-', ' ')} `,
      value: `mdi-${i.name}`,
      prependIcon: `mdi-${i.name}`,
    }))
  })

  async function save() {
    isSaving.value = true

    const payload: Partial<Service> = {
      name: draftName.value,
      icon: draftIcon.value,
      context: draftContext.value,
      editors: draftEditors.value,
      summary: draftSummary.value,
      category: draftCategory.value,
      description: draftDescription.value,
    }

    if (isOwner.value) {
      payload.owner = draftOwner.value
    }

    try {
      await setDoc(doc(firestore, 'service-registry', props.service.id), payload, { merge: true })

      emits('close')
    } finally {
      isSaving.value = false
    }
  }

  function validateOwnerEmail(value: string) {
    return ouraEmailRegex.test(value) || 'Must be a *@ouraring.com email'
  }

  function validateEditorEmails(values: string[]) {
    return values.every((v) => ouraEmailRegex.test(v)) || 'All entries must be *@ouraring.com emails'
  }

  function normalizeEditorEmails(values: string[]) {
    const split = values.flatMap((v) =>
      v
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    )
    return [...new Set(split)]
  }

  function isPublishedTo(stage: AppEnvironment) {
    return props.service.public === stage
  }

  function isAddedTo(stage: AppEnvironment) {
    return Object.keys(props.service.entry || {})?.includes(stage) ?? false
  }

  function mapPublishState<T>(
    stage: AppEnvironment,
    { published, added, notAdded }: { published: T; added: T; notAdded: T },
  ): T {
    if (isPublishedTo(stage)) {
      return published
    }

    if (isAddedTo(stage)) {
      return added
    }

    return notAdded
  }

  function previousStage(stage: AppEnvironment) {
    const index = PUBLISH_STAGES.indexOf(stage)
    return index > 0 ? PUBLISH_STAGES[index - 1] : null
  }

  function isRequestableStage(stage: AppEnvironment) {
    return (PUBLISH_REQUESTABLE_STAGES as readonly string[]).includes(stage)
  }

  function isAddableStage(stage: AppEnvironment) {
    return (ADDABLE_STAGES as readonly string[]).includes(stage)
  }

  function needsAdd(stage: AppEnvironment) {
    return isAddableStage(stage) && !isPublishedTo(stage) && !isAddedTo(stage)
  }

  function canAddTo(stage: AppEnvironment) {
    if (!needsAdd(stage)) return false

    const previous = previousStage(stage)

    return !previous || isAddedTo(previous)
  }

  function canPublishTo(stage: AppEnvironment) {
    return !isPublishedTo(stage) && (isAddedTo(stage) || !isAddableStage(stage))
  }

  const adding = ref<AppEnvironment | null>(null)
  const addError = ref(false)

  const addDialog = ref(false)
  const addStage = ref<AppEnvironment | null>(null)

  function openAdd(stage: AppEnvironment) {
    addStage.value = stage
    addDialog.value = true
  }

  async function addService(stage: AppEnvironment) {
    adding.value = stage
    addError.value = false

    try {
      await httpsCallable(
        getFunctions(firebaseApp, gcpRegion),
        'publishServiceInPrivateMode',
      )({
        serviceId: props.service.id,
        stage,
      })

      addDialog.value = false
    } catch (e) {
      console.error(e)
      addError.value = true
    } finally {
      adding.value = null
    }
  }

  const unpublishing = ref(false)
  const unpublishError = ref(false)
  const unpublishDialog = ref(false)
  const unpublishStage = ref<AppEnvironment | null>(null)

  function openUnpublish(stage: AppEnvironment) {
    unpublishStage.value = stage
    unpublishDialog.value = true
  }

  async function unpublish(stage: AppEnvironment) {
    unpublishing.value = true
    unpublishError.value = false

    try {
      await httpsCallable(
        getFunctions(firebaseApp, gcpRegion),
        'publishServiceInPrivateMode',
      )({
        serviceId: props.service.id,
        stage,
        action: 'unpublish',
      })

      unpublishDialog.value = false
    } catch (e) {
      console.error(e)
      unpublishError.value = true
    } finally {
      unpublishing.value = false
    }
  }

  const isDeletable = computed(() => servicesStore.isDeletable(props.service))

  const deleting = ref(false)
  const deleteError = ref(false)
  const deleteDialog = ref(false)

  async function deleteService() {
    deleting.value = true
    deleteError.value = false

    try {
      await httpsCallable(
        getFunctions(firebaseApp, gcpRegion),
        'archiveRepositoryInGitHub',
      )({ serviceId: props.service.id })

      deleteDialog.value = false

      emits('close')
    } catch (e) {
      console.error(e)
      deleteError.value = true
    } finally {
      deleting.value = false
    }
  }
</script>

<template lang="html">
  <v-card-text>
    <v-form v-model="isFormValid">
      <h2 class="text-title-medium mt-0">Type</h2>

      <v-row>
        <v-col cols="12">
          <v-select
            v-model="draftContext"
            v-model:menu="typeMenuOpen"
            persistent-hint
            hint="This determines how OuraHub handles the app / tool"
            :items="contextTypes"
          />
        </v-col>
      </v-row>

      <h2 class="text-title-medium">Info</h2>

      <v-row>
        <v-col md="6" cols="12">
          <v-text-field
            readonly
            persistent-hint
            label="Application ID"
            hint="Can not be changed after creation"
            :model-value="props.service.id"
          />
        </v-col>

        <v-col md="6" cols="12">
          <v-autocomplete
            v-model="draftIcon"
            item-props
            persistent-hint
            label="Icon"
            hint="Pick the most describing icon for your application"
            :items="materialIcons"
            :rules="[
              requiredRule,
              (v) => existingIconRule(otherServices, v),
              (v) => existingIconsRule(servicesStore.categoriesList!.core, v),
            ]"
          >
            <template #selection="{ item }">
              <v-icon size="24" class="mr-2" :icon="item.value" />
              <span>{{ item.title }}</span>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col md="6" cols="12">
          <v-text-field
            v-model="draftName"
            persistent-hint
            label="Application Name"
            hint="Short name for the prototype (10-25 characters)"
            :rules="[requiredRule, applicationNameRule, (v) => existingNameRule(otherServices, v)]"
          />
        </v-col>

        <v-col md="6" cols="12">
          <v-select
            v-model="draftCategory"
            persistent-hint
            label="Category"
            item-title="name"
            item-value="value"
            hint="Select best fitting category for your application"
            :rules="[requiredRule]"
            :items="servicesStore.categoriesList?.core ?? []"
          >
            <template #selection="{ item }">
              <v-icon size="24" class="mr-2" :icon="item.icon" />
              <span>{{ item.name }}</span>
            </template>
          </v-select>
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="draftSummary"
            persistent-hint
            label="Summary"
            hint="One line summary of the purpose (50-100 characters)"
            :rules="[requiredRule, applicationSummaryRule]"
          />
        </v-col>

        <v-col cols="12">
          <v-textarea
            v-model="draftDescription"
            persistent-hint
            rows="5"
            label="Description"
            hint="Detailed description of the purpose"
          />
        </v-col>
      </v-row>

      <h2 class="text-title-medium mt-6">Maintainers</h2>

      <v-row>
        <v-col cols="12">
          <v-text-field
            v-model.trim="draftOwner"
            persistent-hint
            label="Owner"
            placeholder="user@ouraring.com"
            :readonly="!isOwner"
            :rules="[validateOwnerEmail]"
            :hint="isOwner ? 'You can transfer the ownership by editing this value' : 'Only the owner can change this.'"
          />
        </v-col>

        <v-col cols="12">
          <v-combobox
            chips
            multiple
            closable-chips
            persistent-hint
            label="Collaborators"
            placeholder="user@ouraring.com"
            hint="Press enter to add email, separate multiple emails with a comma or space."
            :model-value="draftEditors"
            :rules="[validateEditorEmails]"
            @update:model-value="draftEditors = normalizeEditorEmails($event)"
          />
        </v-col>
      </v-row>
    </v-form>

    <div ref="publishing" data-intro-tour-id="publish-timeline">
      <h2 class="text-title-medium mt-6">Versions</h2>

      <ul class="text-body-2 text-medium-emphasis mb-2 pl-4">
        <li>
          <b>Creating</b>
          a version
          <ul>
            <li>
              Adds a new
              <b>private</b>
              version for OuraHub launch menu
            </li>
            <li>Private versions are only accessible to the maintainers of the app / tool</li>
          </ul>
        </li>

        <li>
          <b>Selecting</b>
          a version as
          <b>public</b>
          <ul>
            <li>Makes the selected version available to all OuraHub users</li>
            <li>Only one version can be public at a time, but it can be changed</li>
            <li>Selecting a public version waits for approval from the OuraHub reviewers</li>
          </ul>
        </li>

        <li>
          <b>Version / environment</b>
          limitations
          <ul>
            <li>Development version can access data from debug flavor app and generated data</li>
            <li>Experimental version can access data from staging and experimental app flavors</li>
            <li>Production version can access data from release and old research app flavors</li>
            <li>In addition all versions can access the data of lower versions / environments</li>
            <li>Creating production version will always need a security review for the code</li>
          </ul>
        </li>
      </ul>

      <v-timeline
        side="end"
        class="mt-8"
        density="compact"
        truncate-line="both"
        direction="horizontal"
        style="grid-auto-columns: 1fr"
      >
        <v-timeline-item
          v-for="stage in PUBLISH_STAGES"
          :key="stage"
          fill-dot
          size="small"
          icon-color="white"
          :dot-color="
            mapPublishState(stage, {
              published: 'success',
              added: 'warning',
              notAdded: 'error',
            })
          "
          :icon="
            mapPublishState(stage, {
              published: 'mdi-check',
              added: 'mdi-account-hard-hat-outline',
              notAdded: 'mdi-close',
            })
          "
        >
          <div class="text-center" :data-intro-tour-id="`publish-${stage}`">
            <div class="font-weight-medium">{{ STAGE_VERSION_NAMES[stage] }}</div>

            <div class="text-caption text-medium-emphasis">{{ stage }} environment</div>

            <div class="text-caption" :class="{ 'font-weight-bold': isPublishedTo(stage) }">
              {{
                mapPublishState(stage, {
                  published: 'Public',
                  added: 'Private',
                  notAdded: 'Not created',
                })
              }}
            </div>

            <template v-if="needsAdd(stage)">
              <o-btn-primary
                v-if="canAddTo(stage)"
                class="mt-1"
                text="Create"
                :loading="adding === stage"
                @click="openAdd(stage)"
              />

              <span v-else class="text-body-medium text-medium-emphasis">
                Create {{ STAGE_VERSION_NAMES[previousStage(stage)!] }} version first
              </span>
            </template>

            <span
              v-if="!isRequestableStage(stage) && !isPublishedTo(stage) && !isAddedTo(stage)"
              class="text-body-medium text-medium-emphasis"
            >
              Creating a {{ STAGE_VERSION_NAMES[stage] }} version is not supported yet
            </span>

            <span v-else-if="!isRequestableStage(stage)" class="text-body-medium text-medium-emphasis">
              Selecting the {{ STAGE_VERSION_NAMES[stage] }} version as public is not supported yet
            </span>

            <o-btn-primary
              v-else-if="canPublishTo(stage)"
              class="mt-1"
              text="Select as public"
              @click="emits('publish', stage)"
            />

            <o-btn-secondary
              v-else-if="isPublishedTo(stage)"
              class="mt-1"
              text="Make private"
              :loading="unpublishing && unpublishStage === stage"
              @click="openUnpublish(stage)"
            />
          </div>
        </v-timeline-item>
      </v-timeline>
    </div>

    <template v-if="isDeletable">
      <h2 class="text-title-medium mt-6 text-error">Danger Zone</h2>

      <p class="text-body-2 text-medium-emphasis mb-2">
        Deleting a prototype removes it from OuraHub, archives its GitHub repository and removes its deployed code from
        all environments. This cannot be undone.
      </p>

      <v-tooltip location="top" text="Only the owner can delete this prototype." :disabled="isOwner">
        <template #activator="{ props: tooltipProps }">
          <div v-bind="tooltipProps" class="d-inline-block">
            <o-btn-error
              text="Delete prototype"
              prepend-icon="mdi-delete"
              :disabled="!isOwner"
              @click="deleteDialog = true"
            />
          </div>
        </template>
      </v-tooltip>
    </template>
  </v-card-text>

  <v-card-actions>
    <o-btn-text text="Cancel" class="px-4 mr-2" :disabled="isSaving" @click="emits('close')" />

    <o-btn-primary text="Save" class="px-4" :loading="isSaving" :disabled="!isFormValid" @click="save()" />
  </v-card-actions>

  <ServiceVersion
    v-if="addStage"
    v-model="addDialog"
    :stage="addStage"
    :loading="adding === addStage"
    :service-name="props.service.name ?? props.service.id"
    @confirm="addService(addStage)"
  />

  <v-snackbar v-model="addError" color="error" location="end bottom">
    Creating the version failed. Please try again or report this in #help-oura-tooling.
  </v-snackbar>

  <ServiceUnpublish
    v-if="unpublishStage"
    v-model="unpublishDialog"
    :stage="unpublishStage"
    :loading="unpublishing"
    :service-name="props.service.name ?? props.service.id"
    @confirm="unpublish(unpublishStage)"
  />

  <v-snackbar v-model="unpublishError" color="error" location="end bottom">
    Making the version private failed. Please try again or report this in #help-oura-tooling.
  </v-snackbar>

  <ServiceDelete
    v-model="deleteDialog"
    :loading="deleting"
    :service-name="props.service.name ?? props.service.id"
    @confirm="deleteService()"
  />

  <v-snackbar v-model="deleteError" color="error" location="end bottom">
    Deleting the prototype failed. Please try again or report this in #help-oura-tooling.
  </v-snackbar>
</template>
